import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../../../app/store';
import { SessionStatus } from '../sessionBoundary/sessionBoundary.constants';
import { IssueReportRecord, IssueReportSource } from './issueReport.props';

const selectIssueReport = (state: RootState) => state.issueReport;

/** Reports in the order they were filed. */
const selectIssueReports = createSelector(
  selectIssueReport,
  (state): IssueReportRecord[] =>
    state.order.map((id) => state.byId[id]).filter(Boolean)
);

/** The record inside the open flag window — what the panel acts on. */
const selectActiveIssueReport = createSelector(
  selectIssueReport,
  (state): IssueReportRecord | null =>
    state.activeReportId ? state.byId[state.activeReportId] ?? null : null
);

const selectActiveFlagWindowExpiresAt = createSelector(
  selectIssueReport,
  (state) => state.activeWindowExpiresAt
);

/** Banked remainder while paused; null while the countdown is running. */
const selectFlagWindowRemainingMs = createSelector(
  selectIssueReport,
  (state) => state.remainingMs
);

/**
 * The sources that can file a report ALREADY CARRYING a type the agent never chose.
 *
 * Exported so the gestures and this selector cannot drift apart silently: a gesture
 * that seeds a type and is missing here looks classified to every backlog surface.
 */
export const TYPE_SEEDING_SOURCES: ReadonlySet<IssueReportSource> =
  new Set<IssueReportSource>([
    // A word click seeds Incorrect Transcription; empty-transcript seeds Missing.
    'transcript_turn',
    // HITL-1655.
    'ai_suggestion',
    'order_status',
  ]);

/**
 * Reports that still need something from the agent, NEWEST FIRST.
 *
 * The one question the issue-reporting UI asks, and therefore the one selector. The
 * badge counts these, the Issue-button popover lists them and the task-router lobby
 * card lists them; every surface is answering "what have I not finished?".
 *
 * ## Why this replaced five session-scoped selectors
 *
 * HITL-1501 scoped the button badge to the CURRENT SESSION — "how many have I filed
 * for the car in front of me" — which needed the frozen `envelopeIdentity.session_id`
 * matched against a live resolver, plus a separate `sessionStatus === start` check,
 * because after a car departs both `endedSessionId` and `taskAssignedSessionId` still
 * resolve to the session that just ended. Five selectors and a subtle liveness rule
 * existed to answer that question.
 *
 * HITL-1574 asks a different question, and it makes all of that dead weight:
 * `selectSessionIssueReports`, `selectSessionIssueReportCount`,
 * `selectSessionIssueReportsNewestFirst`, `selectPriorSessionIssueReports` and
 * `selectIsSessionLive` are deleted. `resolveIssueReportSessionId` STAYS — the
 * envelope is still built with it; it is only no longer used to filter for display.
 *
 * ## It deliberately reads NO session state
 *
 * Shift-scoped, not session-scoped, and that is the property the lobby depends on
 * entirely: on the task-router lobby every rung of `resolveIssueReportSessionId` is
 * empty and `sessionStatus` is not `start`, so anything session-filtered returns
 * nothing there. The backlog has to survive the car leaving — that is the whole point
 * of it — so it is derived from the records alone.
 *
 * Bounded already, with no new state: `MAX_RETAINED_REPORTS` caps the store at 50 and
 * `user/logout` wipes it, which is what makes "shift" the right word.
 *
 * ## The predicate, both clauses
 *
 * `issue_type === null` is the obvious half: nobody classified it.
 *
 * The second half is that a report whose type was SEEDED BY A GESTURE counts as
 * outstanding even though it HAS a type, because that type is a machine's guess the
 * agent never confirmed. `TranscriptMessage` picks between the two ASR values purely
 * from which line was clicked, `LiveTranscriptCard` always files
 * `Missing Transcription`, and HITL-1655's suggestion and banner gestures seed
 * `Incorrect Response` and `Order Rejected by POS`. In every case the agent chose to
 * flag, not what to flag. So the row opens with its chip pre-selected and the agent
 * confirms or changes it.
 *
 * ## Why this is a SOURCE set and not just `!type_confirmed`
 *
 * `!type_confirmed` alone reads better and is wrong. `commitIssueType` takes
 * `classified_from` optionally, and only a non-null value sets `type_confirmed` — so a
 * commit that names a type WITHOUT naming a surface classifies the report while leaving
 * it unconfirmed. That path is real and is treated as classified today. Keying the
 * whole predicate on confirmation would silently pin every such report in the backlog
 * for the rest of the shift.
 *
 * The set is the honest statement of the rule: these are the sources that can arrive
 * carrying a type nobody chose. ADD A SOURCE HERE WHENEVER A NEW GESTURE SEEDS A TYPE —
 * omitting it means the report leaves the backlog with a guess no human ever saw, which
 * is silent and invisible. `issueReport.slice.test.ts` asserts every seeding source is
 * listed.
 *
 * (HITL-1655's cart-row gesture seeds an ITEM but no type, so it is deliberately NOT
 * here — an item says which thing, never what is wrong with it, and the report lands
 * unclassified and is caught by the first clause.)
 *
 * Confirmation is `type_confirmed`, set by a chip click and never cleared. This clause
 * read `revision === 0` first, and that WAS a bug: `reportAmended` bumps the revision
 * for every amend, so a word-click report where the agent typed a detail and pressed
 * `Later` amended to revision 1 and silently left this list — it had been classified by
 * nobody, and the surface that existed to bring it back no longer showed it. The
 * revision says *something happened*; only `type_confirmed` says *a human chose the
 * type*.
 *
 * Confirming even an UNCHANGED chip still amends, which costs nothing and buys
 * something on the wire: a later envelope carrying a type identical to revision 0, with
 * `classified_from` set, is precisely "a human confirmed the machine's guess", so
 * machine-picked and agent-confirmed stay distinguishable server-side.
 *
 * `!voided` excludes a retracted report: `Discard` sets it, which is why discarding a
 * row removes it from every surface with no extra code.
 *
 * ## Known regression, named rather than hidden
 *
 * Nothing shows already-CLASSIFIED reports any more — the button's history tooltip and
 * the panel's summary line both went with the selectors above. So an agent can re-file
 * something they already handled with no warning. Accepted: the duplicate is harmless
 * on the wire (consumers dedupe on `(issue_id, revision)` and these are separate
 * reports), and a surface that answers "what is left" is worth more mid-shift than one
 * that answers "what have I done".
 */
const selectOutstandingIssueReports = createSelector(
  selectIssueReports,
  (reports): IssueReportRecord[] =>
    reports
      .filter(
        (report) =>
          !report.voided &&
          (report.issue_type === null ||
            (TYPE_SEEDING_SOURCES.has(report.source) && !report.type_confirmed))
      )
      // Newest first. Safe in place — `filter` already produced a fresh array — and
      // memoized here rather than reversed at each call site, where a new array every
      // render would defeat the memoization downstream.
      .reverse()
);

/**
 * The outstanding backlog for THIS SESSION — what the console counts.
 *
 * The console and the lobby ask different questions, and until now both got the same
 * answer. On the HITL screen the agent is working ONE car, and a badge carrying the
 * last three cars' unfinished reports is noise against the car in front of them. On
 * the task-router lobby there is no car at all, and the whole point of the card is the
 * leftovers — so the lobby keeps `selectOutstandingIssueReports`, unfiltered.
 *
 * ## Why `sessionBoundary.sessionId` alone, and not `resolveIssueReportSessionId`
 *
 * The resolver falls through four rungs, and the last two are what made session
 * scoping fragile enough for HITL-1574 to delete it: after a car departs both
 * `endedSessionId` and `taskAssignedSessionId` still resolve to the session that just
 * ended, so "the current session" kept answering with the previous one.
 *
 * `sessionId` has none of that. `fillEndSessionValues` moves it to `endedSessionId`
 * and sets it to `''` the moment the session ends, and session start overwrites it.
 * So it is EITHER the live session or empty, never the last one — which means it is
 * both the filter and the liveness check, with no second predicate to keep in step.
 *
 * ## Empty session id means ALL outstanding, not none
 *
 * The first cut returned `[]` between cars, and an existing test caught why that is
 * wrong: the idle gap between cars is precisely when the agent classifies, and it is
 * the whole reason HITL-1571 built the popover. Not every agent is on the task
 * router, so emptying the console badge between cars would have made the previous
 * car's reports unreachable ANYWHERE for those agents — scoping that is really
 * deletion.
 *
 * So the badge answers "what is outstanding that I can act on right now": narrowed to
 * the car while there is one, everything the moment there is not. The complaint this
 * fixes is old counts showing DURING a new car, and that is exactly what it fixes.
 *
 * ## The known mismatch, and why it fails this way round
 *
 * Reports are STAMPED with the four-rung resolver, so a report could in principle
 * carry `taskAssignedSessionId` while this reads `sessionBoundary.sessionId`. For one
 * car those are the same session id — task assignment and session start are driven by
 * the same event — so they agree in the normal case. If they ever diverge, this hides
 * a report that belongs to the live car rather than showing one that does not, and
 * the report is still reachable on the lobby card. That is the cheaper mistake, but it
 * IS a mistake: if agents report a just-filed issue missing from the badge, this is
 * the line to suspect.
 *
 * Not session-filtered anywhere else. `commitIssueType` and `voidIssue` keep working
 * on any report from any session, because the lobby classifies exactly those.
 */
const selectSessionOutstandingIssueReports = createSelector(
  selectOutstandingIssueReports,
  // Read directly rather than through `selectSessionStatus`, for the cycle reason
  // documented on `selectCanFileIssueReport` below.
  (state: RootState) => state.sessionBoundary?.sessionId,
  (reports, liveSessionId): IssueReportRecord[] =>
    liveSessionId
      ? reports.filter(
          (report) => report.envelopeIdentity.session_id === liveSessionId
        )
      : reports
);

/**
 * Whether a NEW report may be filed right now.
 *
 * `isCarPresent || sessionStatus === 'start'` — read as **is there an active order
 * interaction**, which is not the same question as "is a car at the board".
 *
 * ## What each state means here
 *
 * `sessionStatus` has THREE live values, and the third is the one that makes this
 * predicate work: `''` (no session has ever started on this console), `'start'`, and
 * `'end'` (the car left AND `checkSessionEnd` passed). Both `''` and `'end'` block.
 *
 * `'start'` outlives `car_exit` while an order tail is unsettled — a check was created
 * and the final order is not submitted yet, or the cart is not empty. That state is an
 * agent still working the departed car's order, and a defect they file then is ABOUT
 * that order: it carries a real `session_id` and a real frozen context. So it allows.
 *
 * ## Why not simply `isCarPresent`
 *
 * Because that fails CLOSED in the one direction that matters. `isCarPresent` is set by
 * the `car_enter` frame (and by the task-router hook's local `carEnter` action on
 * task_assigned). A missed or late frame would leave an agent unable to report a defect
 * with a car in front of them — while the session is open and everything else works.
 * Including `sessionStatus` means the gate degrades to "allowed" rather than "blocked"
 * when a signal is lost, which is the right direction for a rule whose only job is
 * suppressing junk.
 *
 * It also costs the TRAILING report: wrong total, wrong readback and unnecessary
 * intervention are all things an agent registers as the car pulls away.
 *
 * ## The accepted hole
 *
 * An agent who never settles an order tail — never presses Finish or Cancel Order —
 * keeps the ability to file indefinitely, because nothing forces `checkSessionEnd` to
 * pass. Known and accepted: that state is an order nobody closed, which is itself worth
 * reporting, and closing the hole would mean new state (a car-exit timestamp) plus a
 * tunable grace window.
 *
 * ## Scope — this gates FILING ONLY
 *
 * Not classifying, not retracting. `commitIssueType` and `voidIssue` must keep working
 * with no car and no session: the outstanding backlog exists precisely to be worked
 * BETWEEN cars, from the Issue button's popover and the task-router lobby. If a fixture
 * with no car ever has to gain one to keep a classification test green, this gate has
 * been applied too widely.
 */
const selectCanFileIssueReport = createSelector(
  // Read off the state directly rather than through `selectIsCarPresent` /
  // `selectSessionStatus`, and NOT because it is shorter. `selectors/message` and
  // `reducers/messagingSlice` import each other, so importing that module here drags
  // this file — which `issueReport.slice` now imports — into that cycle, and
  // `createSelector` then runs with an undefined input selector at module init. The
  // failure mode is the thunk throwing on every press.
  //
  // `resolveIssueReportSessionId` reads `state.sessionBoundary` / `state.taskRouter`
  // the same way, for the same reason. What must not be duplicated is the RULE, and
  // that lives once, below.
  (state: RootState) => state.messages?.isCarPresent,
  (state: RootState) => state.sessionBoundary?.sessionStatus,
  (isCarPresent, sessionStatus): boolean =>
    Boolean(isCarPresent) || sessionStatus === SessionStatus.start
);

export {
  selectActiveFlagWindowExpiresAt,
  selectActiveIssueReport,
  selectCanFileIssueReport,
  selectIssueReport,
  selectIssueReports,
  selectFlagWindowRemainingMs,
  selectOutstandingIssueReports,
  selectSessionOutstandingIssueReports,
};
